import { Alert, Box, CircularProgress, Paper } from '@mui/material'
// react-pdf pins pdfjs-dist 2.16.105. Use that same worker locally.
// eslint-disable-next-line import/no-extraneous-dependencies
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.js?url'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'

type Props = {
  hash?: string
  onFailure: () => void
  onReady: () => void
  url: null | string
}

const AgreementPdfPreview = ({ hash, onFailure, onReady, url }: Props) => {
  const { t } = useTranslation()
  const container = useRef<HTMLDivElement>(null)
  const callbacks = useRef({ onFailure, onReady })
  callbacks.current = { onFailure, onReady }
  const [width, setWidth] = useState(320)
  const [file, setFile] = useState<string>()
  const [pages, setPages] = useState(0)
  const [failed, setFailed] = useState(false)
  const [options, setOptions] = useState<{
    isEvalSupported: boolean
    worker: pdfjs.PDFWorker
  }>()
  const currentFile = useRef<string>()
  const rendered = useRef(false)
  const live = useRef(false)
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(120, entry.contentRect.width - 24))
    )
    if (container.current) observer.observe(container.current)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    let active = true
    let blobUrl: string | undefined
    live.current = true
    const controller = new AbortController()
    const worker = new Worker(workerUrl)
    const pdfWorker = pdfjs.PDFWorker.fromPort({ port: worker })
    const timer = window.setTimeout(() => controller.abort(), 30000)
    rendered.current = false
    currentFile.current = undefined
    setFile(undefined)
    setFailed(false)
    setPages(0)
    setOptions({ isEvalSupported: false, worker: pdfWorker })
    const fail = () => {
      if (active) {
        setFailed(true)
        callbacks.current.onFailure()
      }
    }
    ;(async () => {
      try {
        if (!url) throw new Error('Unavailable')
        const response = await fetch(url, {
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Unavailable')
        const bytes = await response.arrayBuffer()
        if (hash) {
          const digest = await crypto.subtle.digest('SHA-256', bytes)
          const actual = Array.from(new Uint8Array(digest), (byte) =>
            byte.toString(16).padStart(2, '0')
          ).join('')
          if (actual !== hash) throw new Error('Unavailable')
        }
        if (!active) return
        blobUrl = URL.createObjectURL(
          new Blob([bytes], { type: 'application/pdf' })
        )
        currentFile.current = blobUrl
        setFile(blobUrl)
      } catch {
        fail()
      } finally {
        window.clearTimeout(timer)
      }
    })()
    return () => {
      active = false
      controller.abort()
      window.clearTimeout(timer)
      live.current = false
      currentFile.current = undefined
      if (blobUrl) URL.revokeObjectURL(blobUrl)
      pdfWorker.destroy()
      worker.terminate()
    }
  }, [hash, url])
  const onError = () => {
    if (live.current && file && currentFile.current === file) {
      setFailed(true)
      callbacks.current.onFailure()
    }
  }
  return (
    <Paper ref={container} sx={{ height: 400, overflow: 'auto', p: 1 }}>
      {failed && <Alert severity="error">{t('agreement.preview_error')}</Alert>}
      {!failed && (!file || !options) && (
        <CircularProgress aria-label={t('agreement.loading')} />
      )}
      {!failed && file && options && (
        <Document
          file={file}
          onLoadError={onError}
          onLoadSuccess={({ numPages }) => {
            if (live.current && currentFile.current === file) setPages(numPages)
          }}
          options={options}
        >
          {Array.from({ length: pages }, (_, index) => (
            <Box key={`page-${index + 1}`} sx={{ mb: 1 }}>
              <Page
                onRenderError={onError}
                onRenderSuccess={() => {
                  if (
                    live.current &&
                    currentFile.current === file &&
                    index === 0 &&
                    !rendered.current
                  ) {
                    rendered.current = true
                    callbacks.current.onReady()
                  }
                }}
                pageNumber={index + 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={width}
              />
            </Box>
          ))}
        </Document>
      )}
    </Paper>
  )
}

export default AgreementPdfPreview
